import { AnyAction } from 'redux'
import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import {
  BulkUploadFailedMessage,
  BulkUploadFileSizeError,
  BulkUploadSuccessMessage,
  DefaultSuccesrOnSubmitData,
  RequestFailureMessage,
  SingleScoreSuccessMessage,
} from '../../../common/utils/messagesContants'
import { AxiosError, AxiosResponse } from 'axios'
import { camelize, snakeize } from 'casing'
import { getErrorMessages } from '../../../common/utils/error'
import {
  Chat,
  ChatCTA,
  Conversation,
  ConversationResponse,
  FiltersState,
  FilterType,
  InferenceLimitUpdate,
  Limit,
  OnboardingStatusResponse,
  Recommendations,
  PropensityPayload,
  SellerUrlUpdate,
  UserConfiguration,
} from './reducer'
import { actionTypeWrapper } from '../../../common/utils/actionTypeWrapper'
import { AppDispatch, RootState } from '../../../store'
import { snakeCase } from 'lodash'
import { errorLogger } from '../../../common/utils/errorLogger'
import {
  calculatePropensityScore,
  fetchPropensityData,
  fetchStatus,
  getPreSignedUrlIntelligence,
  getInferenceLimit,
  postPtb,
  putInferenceLimit,
  validateCsvFile,
  add_seller,
  sendPromptToAgent,
  fetchConversations,
  getChats,
  getRecommendations,
  checkOnboardingStatus,
  fetchAllIcps,
  updateIcpState,
  getUserConfigurations,
  putUserConfigurations,
} from '../../api'
import { createTypingSSEConnection } from '../../../common/utils/sseHelper'
import {
  normalizeStreamingResponse,
  StreamingEvent,
} from '../../../common/utils/responseNormalizer'
import { LastEvaluatedObject } from '../../../flyout/modules/revenueInsightsV2/reducer'
import { convertObjectKeysAndValuesToCommaSeprated } from '../../../common/utils/helperFunctions'
import { newrelicErrLogger } from '../../../common/utils/ErrorHandler'
import { isEmpty } from 'lodash'
import { getSearchedData } from '../../../flyout/api/markeplace'
import FileDownload from 'js-file-download'
import { ICPData } from '../PartnerIQ/reducer'
import { setAllIcpData, updateIcpInList } from '../AllIcps/action'

let currentSSECancelFunction: (() => void) | null = null

export const cancelCurrentSSEConnection = () => {
  if (currentSSECancelFunction) {
    currentSSECancelFunction()
    currentSSECancelFunction = null
  }
}

// Helper function to create a StreamingEvent from an SSE event
const createStreamingEvent = (
  event: any,
  eventType?: string
): StreamingEvent => ({
  type: eventType || event.data?.params?.eventType || event.type || '',
  params: event.data?.params || {},
  data: event.data,
  timestamp: event.timestamp || event.data?.params?.timestamp,
})

// Helper function to handle stream completion
const handleStreamCompletion = (
  currentMessageId: string,
  collectedEvents: StreamingEvent[],
  getState: () => RootState,
  dispatch: AppDispatch
) => {
  const currentChat = getState().propensity.chats?.find(
    chat => chat.eventId === currentMessageId
  )
  const completedStages = currentChat?.completedStages || []
  const updates: any = {
    agentStage: 'complete',
    stageMessage: undefined,
  }

  if (currentChat?.error) {
    updates.error = currentChat.error
  }

  if (currentChat?.agentStage && currentChat?.stageMessage) {
    const isAlreadyCompleted = completedStages.some(
      cs =>
        cs.stage === currentChat.agentStage &&
        cs.message === currentChat.stageMessage
    )
    if (!isAlreadyCompleted) {
      updates.completedStages = [
        ...completedStages,
        {
          stage: currentChat.agentStage,
          message: currentChat.stageMessage,
          timestamp: new Date().toISOString(),
        },
      ]
    }
  }

  try {
    const normalizedResponse = normalizeStreamingResponse(collectedEvents)
    if (normalizedResponse) {
      updates.content = normalizedResponse
    }
    dispatch(updateChat(currentMessageId, updates))
  } catch (error) {
    console.warn('Failed to normalize streaming response:', error)
    dispatch(updateChat(currentMessageId, updates))
  }
}

// Helper function to determine stage and message from event type
const getStageFromEventType = (
  eventType: string,
  event: any,
  currentChat: Chat | undefined
): {
  stage:
    | 'thinking'
    | 'retrieving'
    | 'using_tool'
    | 'processing'
    | 'responding'
    | 'complete'
    | undefined
  stageMessage: string | undefined
  shouldCompletePreviousStage: boolean
  previousStage:
    | 'thinking'
    | 'retrieving'
    | 'using_tool'
    | 'processing'
    | 'responding'
    | 'complete'
    | undefined
  previousStageMessage: string | undefined
} => {
  let stage:
    | 'thinking'
    | 'retrieving'
    | 'using_tool'
    | 'processing'
    | 'responding'
    | 'complete'
    | undefined
  let stageMessage: string | undefined
  let shouldCompletePreviousStage = false
  let previousStage:
    | 'thinking'
    | 'retrieving'
    | 'using_tool'
    | 'processing'
    | 'responding'
    | 'complete'
    | undefined
  let previousStageMessage: string | undefined

  if (eventType === 'server-tool-use') {
    if (currentChat?.agentStage && currentChat?.stageMessage) {
      previousStage = currentChat.agentStage
      previousStageMessage = currentChat.stageMessage
      shouldCompletePreviousStage = true
    }
    stage = 'using_tool'
    const toolName = event.data?.params?.contentBlock?.content?.name || 'tool'
    const displayActivity =
      event.data?.params?.contentBlock?.content?.displayToolActivity
    stageMessage = displayActivity || `Using ${toolName}...`
  } else if (eventType === 'server-tool-response') {
    if (currentChat?.agentStage === 'using_tool' && currentChat?.stageMessage) {
      previousStage = currentChat.agentStage
      previousStageMessage = currentChat.stageMessage
      shouldCompletePreviousStage = true
    }
    stage = 'processing'
    stageMessage = 'Processing results...'
  } else if (eventType === 'agent_handoff') {
    if (currentChat?.agentStage && currentChat?.stageMessage) {
      previousStage = currentChat.agentStage
      previousStageMessage = currentChat.stageMessage
      shouldCompletePreviousStage = true
    }
    stage = 'thinking'
    stageMessage = 'Routing to specialist...'
  } else if (eventType === 'assistant-response.start') {
    if (currentChat?.agentStage && currentChat?.stageMessage) {
      previousStage = currentChat.agentStage
      previousStageMessage = currentChat.stageMessage
      shouldCompletePreviousStage = true
    }
    stage = 'responding'
    stageMessage = 'Preparing response...'
  } else if (eventType === 'assistant-response.completed') {
    if (currentChat?.agentStage && currentChat?.stageMessage) {
      previousStage = currentChat.agentStage
      previousStageMessage = currentChat.stageMessage
      shouldCompletePreviousStage = true
    }
    stage = 'responding'
    stageMessage = 'Finalizing response...'
  }

  return {
    stage,
    stageMessage,
    shouldCompletePreviousStage,
    previousStage,
    previousStageMessage,
  }
}

// Helper function to handle stage updates
const handleStageUpdate = (
  eventType: string,
  event: any,
  currentMessageId: string | null,
  isStreamComplete: boolean,
  getState: () => RootState,
  dispatch: AppDispatch
) => {
  if (!currentMessageId) return

  const currentChat = getState().propensity.chats?.find(
    chat => chat.eventId === currentMessageId
  )

  const {
    stage,
    stageMessage,
    shouldCompletePreviousStage,
    previousStage,
    previousStageMessage,
  } = getStageFromEventType(eventType, event, currentChat)

  if (eventType === 'server-tool-response') {
    const toolResponseStatus = event.data?.params?.contentBlock?.content?.status
    const toolResponseOutput = event.data?.params?.contentBlock?.content?.output
    if (toolResponseStatus === 'error' && toolResponseOutput) {
      let errorMessage = ''
      try {
        if (typeof toolResponseOutput === 'string') {
          errorMessage = toolResponseOutput
        } else if (typeof toolResponseOutput === 'object') {
          errorMessage = JSON.stringify(toolResponseOutput, null, 2)
        }
      } catch (e) {
        errorMessage = String(toolResponseOutput)
      }

      if (errorMessage && currentMessageId) {
        dispatch(updateChat(currentMessageId, { error: errorMessage }))
      }
    }
  }

  if (stage && !isStreamComplete) {
    const completedStages = currentChat?.completedStages || []
    const updates: any = {
      agentStage: stage,
      stageMessage,
    }

    if (shouldCompletePreviousStage && previousStage && previousStageMessage) {
      const isAlreadyCompleted = completedStages.some(
        cs => cs.stage === previousStage && cs.message === previousStageMessage
      )
      if (!isAlreadyCompleted) {
        updates.completedStages = [
          ...completedStages,
          {
            stage: previousStage,
            message: previousStageMessage,
            timestamp: new Date().toISOString(),
          },
        ]
      }
    }

    dispatch(updateChat(currentMessageId, updates))
  }
}

// Helper function to handle CTA processing
const handleCTAProcessing = (
  event: any,
  currentMessageId: string | null,
  dispatch: AppDispatch
) => {
  if (!currentMessageId) return

  const rawCtas = Array.isArray(event.ctas)
    ? event.ctas
    : Array.isArray(event?.data?.ctas)
      ? event.data.ctas
      : []

  if (!rawCtas.length) return

  const normalizedCtas = rawCtas
    .map((cta: any): ChatCTA | null => {
      if (cta && typeof cta.label === 'string' && cta.prompt) {
        return {
          label: cta.label,
          prompt: cta.prompt,
        }
      }
      return null
    })
    .filter((cta: ChatCTA | null): cta is ChatCTA => Boolean(cta))

  if (normalizedCtas.length) {
    dispatch(
      updateChat(currentMessageId, {
        ctas: normalizedCtas,
      })
    )
  }
}

const calculateEffectiveTotal = (
  isEmptyPage: boolean,
  page: number,
  total: number | undefined,
  newCount: number,
  currentCount: number
): number => {
  if (isEmptyPage) return page <= 1 ? 0 : currentCount
  const calculated = page <= 1 ? newCount : currentCount + newCount
  return Math.max(total ?? 0, calculated)
}

export enum PropensityActionTypes {
  SET_REPORTS_DATA = 'SET_REPORTS_DATA',
  PROPENSITY_SET_PAGE_NUMBER = 'PROPENSITY_SET_PAGE_NUMBER',
  PROPENSITY_SET_LIMIT = 'PROPENSITY_SET_LIMIT',
  SET_PRODUCT_ID = 'SET_PRODUCT_ID',
  SET_DOMAIN_URLS = 'SET_DOMAIN_URLS',
  CLEAR_DOMAIN_URLS = 'CLEAR_DOMAIN_URLS',
  CLEAR_PROPENSITY_REPORTS = 'CLEAR_PROPENSITY_REPORTS',
  SET_FILTERS = 'SET_FILTERS',
  CLEAR_PROPENSITY_FILTERS = 'CLEAR_PROPENSITY_FILTERS',
  SET_DROP_DOWN_KEY = 'SET_DROP_DOWN_KEY',
  CLEAR_DROP_DOWN_KEY = 'CLEAR_DROP_DOWN_KEY',
  SHOW_DEFAULT = 'SHOW_DEFAULT',
  HIDE_DEFAULT = 'HIDE_DEFAULT',
  SHOW_ALERT = 'SHOW_ALERT',
  HIDE_ALERT = 'HIDE_ALERT',
  SET_INFERENCE_LIMIT = 'SET_INFERENCE_LIMIT',
  SET_SELLER = 'SET_SELLER',
  SET_PROMPT_INPUT = 'SET_PROMPT_INPUT',
  CLEAR_PROMPT_INPUT = 'CLEAR_PROMPT_INPUT',
  SET_CHATS = 'SET_CHATS',
  ADD_CHAT = 'ADD_CHAT',
  UPDATE_CHAT = 'UPDATE_CHAT',
  CLEAR_CHATS = 'CLEAR_CHATS',
  SET_ACTIVE_SESSION_ID = 'SET_ACTIVE_SESSION_ID',
  CLEAR_ACTIVE_SESSION_ID = 'CLEAR_ACTIVE_SESSION_ID',
  SET_CONVERSATIONS = 'SET_CONVERSATIONS',
  APPEND_CONVERSATIONS = 'APPEND_CONVERSATIONS',
  ADD_CONVERSATION = 'ADD_CONVERSATION',
  UPDATE_CONVERSATION = 'UPDATE_CONVERSATION',
  CLEAR_CONVERSATIONS = 'CLEAR_CONVERSATIONS',
  GET_CONVERSATIONS = 'GET_CONVERSATIONS',
  SET_CONVERSATIONS_PAGE_INFO = 'SET_CONVERSATIONS_PAGE_INFO',
  SET_CHAT_NEXT_TOKEN = 'SET_CHAT_NEXT_TOKEN',
  CLEAR_CHAT_NEXT_TOKEN = 'CLEAR_CHAT_NEXT_TOKEN',
  PREPEND_CHATS = 'PREPEND_CHATS',
  SET_HAS_MORE_CHATS = 'SET_HAS_MORE_CHATS',
  SET_RECOMMENDATIONS = 'SET_RECOMMENDATIONS',
  CLEAR_RECOMMENDATIONS = 'CLEAR_RECOMMENDATIONS',
  SET_HAS_OPENED_AI_DRAWER = 'SET_HAS_OPENED_AI_DRAWER',
  SET_IS_EXTERNAL_LOADING = 'SET_IS_EXTERNAL_LOADING',
  CLEAR_IS_EXTERNAL_LOADING = 'CLEAR_IS_EXTERNAL_LOADING',
  SET_USER_CONFIGURATIONS = 'SET_USER_CONFIGURATIONS',
}

const handlePropensityFetch = async ({
  partnerId,
  pageSize,
  lastEvaluatedObject,
  pageNumber,
  sectionName,
  selectedInput,
  input,
  filters,
}: {
  partnerId: string
  pageSize: number
  lastEvaluatedObject?: LastEvaluatedObject | null
  selectedInput?: string
  input?: string
  pageNumber?: number
  sectionName?: string
  filters?: FilterType
}) => {
  const formattedLastEvaluatedObject = lastEvaluatedObject
    ? snakeize(lastEvaluatedObject)
    : undefined
  if (isEmpty(input)) {
    const { data } = await fetchPropensityData(
      false,
      partnerId,
      pageSize,
      formattedLastEvaluatedObject,
      filters
    )
    return data
  } else {
    const response = await getSearchedData({
      partnerId,
      input: input,
      selectedInput: selectedInput,
      pageSize,
      pageNumber: pageNumber,
      sectionName: sectionName,
      isRevenueSearch: false,
    })

    const latestRecord = response.data.records
      .map((record: any) => record._source)
      .sort(
        (a: any, b: any) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )[0]

    if (latestRecord) {
      const domainName =
        latestRecord.domain_name ||
        latestRecord.domainName ||
        latestRecord.prospect ||
        ''
      latestRecord.id = `${partnerId}${domainName}`
    }

    const formattedResponse = {
      count: latestRecord ? 1 : 0,
      error: null,
      last_evaluated_object: null,
      records: latestRecord ? [latestRecord] : [],
    }
    return formattedResponse
  }
}

export const getSummary =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { data } = await fetchStatus(partnerId)
      return data
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    }
  }

export const pollSummaryUntilReady =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.PROPENSITY_DATA))
    try {
      const maxAttempts = 12
      const interval = 15000
      let attempt = 0
      let lastSummary = null

      while (attempt < maxAttempts) {
        if (attempt > 0) {
          await new Promise(res => setTimeout(res, interval))
        }

        lastSummary = await dispatch(getSummary(partnerId))
        const inProgress =
          lastSummary?.logs_status_counts?.find(
            (s: any) => s.status === 'IN_PROGRESS'
          )?.count ?? 0

        if (inProgress === 0) {
          return { ready: true, lastSummary }
        }

        attempt++
      }

      return { ready: true, lastSummary }
    } finally {
      await dispatch(stopLoading(LoadingTypes.PROPENSITY_DATA))
    }
  }

export const getPropensityData =
  ({
    partnerId,
    downloadReport,
    pageSizeValue,
    pageNumber,
    lastEvaluatedObject,
    input,
    selectedInput,
    sectionName,
  }: {
    partnerId?: string
    downloadReport?: boolean
    pageSizeValue?: number
    pageNumber?: number
    lastEvaluatedObject?: LastEvaluatedObject | null
    input?: string
    selectedInput?: string
    sectionName?: string
  } = {}) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.PROPENSITY_DATA))

    try {
      if (partnerId) {
        partnerId = partnerId
      } else {
        partnerId = getState().AccountData.user.partnerData?.partnerId || ''
      }
      const { pageSize: pageSizeStateValue } = getState().propensity
      const pageSize = pageSizeValue ?? pageSizeStateValue
      const { filters } = getState().propensity

      const formatFilterValue = (value: string) =>
        value === 'All' ? 'all' : value

      const formatedFilters: FilterType = {
        time_range: filters?.lastUpdated || '',
        marketplaces:
          filters?.marketplace?.map(formatFilterValue).join(',') || '',
        propensity_level:
          filters?.propensityScore?.map(formatFilterValue).join(',') || '',
        aws_level: filters?.aws?.map(formatFilterValue).join(',') || '',
        azure_level: filters?.azure?.map(formatFilterValue).join(',') || '',
        gcp_level: filters?.gcp?.map(formatFilterValue).join(',') || '',
      }

      if (!downloadReport) {
        const data = await handlePropensityFetch({
          partnerId,
          pageSize,
          lastEvaluatedObject,
          selectedInput,
          input,
          pageNumber,
          sectionName,
          filters: formatedFilters,
        })
        await dispatch(setReportsData(camelize(data), pageSize))
      } else {
        await downloadPtbReports({
          downloadReport,
          partnerId,
          pageSize,
          lastEvaluatedObject,
          pageNumber,
          sectionName,
          selectedInput,
          input,
          filters: formatedFilters,
        })
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.PROPENSITY_DATA))
    }
  }

export const downloadPtbReports = async ({
  downloadReport,
  partnerId,
  pageSize,
  lastEvaluatedObject,
  pageNumber,
  sectionName,
  selectedInput,
  input,
  filters,
}: {
  downloadReport?: boolean
  partnerId: string
  pageSize: number
  lastEvaluatedObject?: LastEvaluatedObject | null
  selectedInput?: string
  input?: string
  pageNumber?: number
  sectionName?: string
  filters?: FilterType
}) => {
  const formattedLastEvaluatedObject = lastEvaluatedObject
    ? snakeize(lastEvaluatedObject)
    : undefined
  if (isEmpty(input)) {
    return fetchPropensityData(
      downloadReport,
      partnerId,
      pageSize,
      formattedLastEvaluatedObject,
      filters
    ).then(response => {
      FileDownload(response.data, `${partnerId}.csv`)
    })
  } else {
    return getSearchedData({
      partnerId,
      input,
      selectedInput,
      pageSize,
      pageNumber,
      sectionName,
      downloadToCsv: true,
    }).then(response => {
      FileDownload(response.data, `${partnerId}.csv`)
    })
  }
}

export const postPtbCall =
  (partnerId: string, s3Path: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.PROPENSITY_DATA))
    try {
      const response = await validateCsvFile(partnerId, s3Path)
      if (response.data.status === true) {
        const { data } = await postPtb(partnerId, s3Path)
        dispatch(
          updateAppAlert({
            message: BulkUploadSuccessMessage,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        return data
      } else if (response.data.status === false) {
        dispatch(
          updateAppAlert({
            message: response.data.message,
            messageType: 'ERROR',
            autoClose: false,
          })
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.PROPENSITY_DATA))
    }
  }

export const calculatePtbScore =
  (partnerId: string, domain: string[]) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.PROPENSITY_DATA))
    try {
      const { data } = await calculatePropensityScore(
        partnerId,
        domain.map(prospect => ({ prospect }))
      )
      dispatch(
        updateAppAlert({
          message: SingleScoreSuccessMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return data
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.PROPENSITY_DATA))
    }
  }

export const getPreSignedUrl =
  (partnerId: string, fileKeys: string[]) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))

    try {
      const { data } = await getPreSignedUrlIntelligence(partnerId, fileKeys)
      dispatch(
        updateAppAlert({
          message: DefaultSuccesrOnSubmitData,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return data
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const fetchInferenceLimit =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await getInferenceLimit(partnerId)
      dispatch(setInferenceLimit(snakeize(response.data)))
      dispatch(setSeller(true))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const message = errorResponse?.data?.errors?.[0] || ''

      if (message.toLowerCase().includes('sellers not found')) {
        dispatch(setSeller(false))
      }
      const globalState = getState()

      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const sendPromptToAIAgent =
  (
    partnerId: string,
    data: {
      prompt: string
      user_id: string
      session_id?: string
      conversation_title?: string
      agent_type?: string
    }
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const activeSessionId = getState().propensity.activeSessionId || ''
    await dispatch(startLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
    let loaderStopped = false

    try {
      if (currentSSECancelFunction) {
        currentSSECancelFunction()
        currentSSECancelFunction = null
      }

      const { url, body } = sendPromptToAgent(partnerId, data)
      let sessionId: string | null = null
      let currentMessageId: string | null = null
      let messageCreated = false
      const collectedEvents: StreamingEvent[] = []
      let isStreamComplete = false

      const connection = createTypingSSEConnection(
        { url, body },
        {
          onStart: event => {
            const eventData = createStreamingEvent(event)
            collectedEvents.push(eventData)

            sessionId =
              event.session_id ||
              event.data?.params?.sessionId ||
              event.data?.sessionId ||
              null

            if (sessionId && sessionId !== activeSessionId) {
              dispatch(setActiveSessionId(sessionId))
            }

            const eventType = event.data?.params?.eventType || event.type || ''
            const isStartEvent =
              eventType === 'stream_start' ||
              eventType === 'assistant-response.start' ||
              eventType === 'start'

            if (isStartEvent && !messageCreated) {
              currentMessageId = Date.now().toString()
              messageCreated = true

              dispatch(
                addChat({
                  eventId: currentMessageId,
                  content: '',
                  role: 'AGENT_RESPONSE',
                  timestamp:
                    event.timestamp ||
                    event.data?.params?.timestamp ||
                    new Date().toISOString(),
                  agentStage: 'thinking',
                  stageMessage: 'Initializing...',
                })
              )
            }
          },
          onContent: event => {
            const eventData = createStreamingEvent(
              event,
              'assistant-response.delta'
            )
            collectedEvents.push(eventData)
          },
          onTyping: text => {
            if (currentMessageId) {
              dispatch(updateChat(currentMessageId, { content: text }))
            }
          },
          onTypingComplete: finalText => {
            if (currentMessageId && collectedEvents.length > 0) {
              try {
                const normalizedResponse =
                  normalizeStreamingResponse(collectedEvents)
                if (normalizedResponse) {
                  dispatch(
                    updateChat(currentMessageId, {
                      content: normalizedResponse,
                    })
                  )
                } else if (finalText) {
                  dispatch(updateChat(currentMessageId, { content: finalText }))
                }
              } catch (error) {
                if (finalText) {
                  dispatch(updateChat(currentMessageId, { content: finalText }))
                }
              }
            }
          },
          onCustom: (eventType, event) => {
            const eventData = createStreamingEvent(event, eventType)
            collectedEvents.push(eventData)

            if (
              (eventType === 'stream_end' || eventType === 'end') &&
              currentMessageId &&
              !isStreamComplete
            ) {
              isStreamComplete = true

              if (!loaderStopped) {
                dispatch(stopLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
                loaderStopped = true
              }

              handleStreamCompletion(
                currentMessageId,
                collectedEvents,
                getState,
                dispatch
              )
            }

            handleStageUpdate(
              eventType,
              event,
              currentMessageId,
              isStreamComplete,
              getState,
              dispatch
            )

            if (eventType === 'cta') {
              handleCTAProcessing(event, currentMessageId, dispatch)
            }
          },
          onEnd: event => {
            if (event) {
              const eventData = createStreamingEvent(event, 'end')
              collectedEvents.push(eventData)

              const eventType =
                event.data?.params?.eventType || event.type || ''

              if (
                (eventType === 'stream_end' || eventType === 'end') &&
                currentMessageId &&
                !isStreamComplete
              ) {
                isStreamComplete = true

                if (!loaderStopped) {
                  dispatch(stopLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
                  loaderStopped = true
                }

                handleStreamCompletion(
                  currentMessageId,
                  collectedEvents,
                  getState,
                  dispatch
                )
              }
            }

            currentSSECancelFunction = null
          },
          onError: event => {
            currentSSECancelFunction = null
            throw new Error(event.message || 'Unknown error occurred')
          },
        },
        {
          typingDelay: 80,
          timeout: 300000,
          retryAttempts: 1,
        }
      )

      currentSSECancelFunction = connection.cancel

      await connection.promise
    } catch (error: any) {
      currentSSECancelFunction = null

      console.error('SSE Error:', error)

      dispatch(
        updateAppAlert({
          message: error.message || 'Failed to send prompt to AI agent',
          messageType: 'ERROR',
          autoClose: true,
        })
      )

      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      if (!loaderStopped) {
        await dispatch(stopLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
      }
    }
  }

export const getChatHistory =
  (
    partnerId: string,
    userId: string,
    sessionId: string,
    chatNextToken: string | null
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await getChats(
        partnerId,
        userId,
        sessionId,
        chatNextToken
      )
      dispatch(clearChats())
      dispatch(setActiveSessionId(sessionId))

      if (response.data.has_more) {
        dispatch(setHasMoreChats(true))
        dispatch(setChatNextToken(response.data.next_token))
      } else {
        dispatch(setHasMoreChats(false))
      }
      const sortedMessages = response.data.messages.sort(
        (a: any, b: any) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
      dispatch(setChats(camelize(sortedMessages)))
      return camelize(sortedMessages)
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return []
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const loadMoreChatMessages =
  (partnerId: string, userId: string, sessionId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { chatNextToken, hasMoreChats } = getState().propensity

    if (!hasMoreChats || !chatNextToken) {
      return
    }

    await dispatch(startLoading(LoadingTypes.LOAD_MORE_CHATS))
    try {
      const response = await getChats(
        partnerId,
        userId,
        sessionId,
        chatNextToken
      )
      dispatch(setChatNextToken(response.data.next_token))
      dispatch(setHasMoreChats(response.data.has_more))

      const sortedMessages = response.data.messages.sort(
        (a: any, b: any) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
      dispatch(prependChats(camelize(sortedMessages)))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.LOAD_MORE_CHATS))
    }
  }

export const updateInferenceLimit =
  (partnerId: string, data: InferenceLimitUpdate) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await putInferenceLimit(partnerId, data)
      dispatch(
        updateAppAlert({
          message: DefaultSuccesrOnSubmitData,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return response
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const addSeller =
  (partnerId: string, data: SellerUrlUpdate) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await add_seller(partnerId, data)
      dispatch(
        updateAppAlert({
          message: DefaultSuccesrOnSubmitData,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return response
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const getConversations =
  (
    partnerId: string,
    userId: string,
    page: number = 1,
    pageSize: number = 15
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GET_CONVERSATIONS))
    try {
      const response = await fetchConversations(
        partnerId,
        userId,
        page,
        pageSize
      )
      const data = camelize(response.data)
      const conversations = data.conversations ?? []
      const isEmptyPage = conversations.length === 0
      const currentConversations = getState().propensity.conversations ?? []
      const effectiveTotal = calculateEffectiveTotal(
        isEmptyPage,
        page,
        data.total,
        conversations.length,
        currentConversations.length
      )

      dispatch(
        setConversationsPageInfo({
          page: data.page,
          pageSize: data.pageSize,
          total: effectiveTotal,
        })
      )

      if (page <= 1) {
        dispatch(setConversations(conversations))
      } else {
        dispatch(appendConversations(conversations))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GET_CONVERSATIONS))
    }
  }

export const fetchRecommendations =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await getRecommendations(partnerId)
      const data = camelize(response.data)
      dispatch(setRecommendations(data))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const fetchSubscribed = (partnerId: string) => {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<OnboardingStatusResponse> => {
    await dispatch(startLoading(LoadingTypes.FETCH_SUBSCRIBED))
    try {
      const response = await checkOnboardingStatus(partnerId)
      return response.data as OnboardingStatusResponse
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      throw errorResponse
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_SUBSCRIBED))
    }
  }
}

export const setInferenceLimit = (data: Limit) => ({
  type: PropensityActionTypes.SET_INFERENCE_LIMIT,
  payload: data,
})
export const setSeller = (data: boolean) => ({
  type: PropensityActionTypes.SET_SELLER,
  payload: data,
})
export const setPromptInput = (data: string) => ({
  type: PropensityActionTypes.SET_PROMPT_INPUT,
  payload: data,
})
export const clearPromptInput = () => ({
  type: PropensityActionTypes.CLEAR_PROMPT_INPUT,
})

export const setHasOpenedAiDrawer = (hasOpened: boolean) => ({
  type: PropensityActionTypes.SET_HAS_OPENED_AI_DRAWER,
  payload: hasOpened,
})

export const setChats = (chats: Chat[]) => ({
  type: PropensityActionTypes.SET_CHATS,
  payload: chats,
})

export const addChat = (chat: Chat) => ({
  type: PropensityActionTypes.ADD_CHAT,
  payload: chat,
})

export const updateChat = (
  eventId: string,
  updates: Partial<Omit<Chat, 'eventId'>>
) => ({
  type: PropensityActionTypes.UPDATE_CHAT,
  payload: { eventId, updates },
})

export const setActiveSessionId = (sessionId: string) => ({
  type: PropensityActionTypes.SET_ACTIVE_SESSION_ID,
  payload: sessionId,
})

export const clearActiveSessionId = () => ({
  type: PropensityActionTypes.CLEAR_ACTIVE_SESSION_ID,
})

export const setConversations = (conversations: Conversation[]) => ({
  type: PropensityActionTypes.SET_CONVERSATIONS,
  payload: conversations,
})

export const appendConversations = (conversations: Conversation[]) => ({
  type: PropensityActionTypes.APPEND_CONVERSATIONS,
  payload: conversations,
})

export const clearConversations = () => ({
  type: PropensityActionTypes.CLEAR_CONVERSATIONS,
})

export const setConversationsPageInfo = (data: {
  page: number
  pageSize: number
  total: number
}) => ({
  type: PropensityActionTypes.SET_CONVERSATIONS_PAGE_INFO,
  payload: data,
})

export const clearChats = () => ({
  type: PropensityActionTypes.CLEAR_CHATS,
})

export const setChatNextToken = (chatNextToken: string) => ({
  type: PropensityActionTypes.SET_CHAT_NEXT_TOKEN,
  payload: chatNextToken,
})

export const clearChatNextToken = () => ({
  type: PropensityActionTypes.CLEAR_CHAT_NEXT_TOKEN,
})

export const prependChats = (chats: Chat[]) => ({
  type: PropensityActionTypes.PREPEND_CHATS,
  payload: chats,
})

export const setHasMoreChats = (hasMore: boolean) => ({
  type: PropensityActionTypes.SET_HAS_MORE_CHATS,
  payload: hasMore,
})

export const setRecommendations = (data: Recommendations | null) => ({
  type: PropensityActionTypes.SET_RECOMMENDATIONS,
  payload: data,
})

export const clearRecommendations = () => ({
  type: PropensityActionTypes.CLEAR_RECOMMENDATIONS,
})
export const setReportsData = (data: PropensityPayload, pageSize: number) => ({
  type: PropensityActionTypes.SET_REPORTS_DATA,
  payload: { ...data, pageSize },
})

export const showDefault = () => ({
  type: PropensityActionTypes.SHOW_DEFAULT,
})

export const hideDefault = () => ({
  type: PropensityActionTypes.HIDE_DEFAULT,
})

export const setPropensityPageSize = (pageSize: number) => ({
  type: PropensityActionTypes.PROPENSITY_SET_LIMIT,
  payload: pageSize,
})

export const setPropensityPageNumber = (pageNumber: number) => ({
  type: PropensityActionTypes.PROPENSITY_SET_PAGE_NUMBER,
  payload: pageNumber,
})

export const clearPropensityReports = () => ({
  type: PropensityActionTypes.CLEAR_PROPENSITY_REPORTS,
})

export const setDomainUrls = (domainUrls: string[]) => ({
  type: PropensityActionTypes.SET_DOMAIN_URLS,
  payload: domainUrls,
})

export const clearDomainUrls = () => ({
  type: PropensityActionTypes.CLEAR_DOMAIN_URLS,
})

export const setFilters = (filters: FiltersState) => ({
  type: PropensityActionTypes.SET_FILTERS,
  payload: filters,
})

export const showAlert = () => ({
  type: PropensityActionTypes.SHOW_ALERT,
})

export const hideAlert = () => ({
  type: PropensityActionTypes.HIDE_ALERT,
})

export const setDropDownKey = (dropDownKey: string) => ({
  type: PropensityActionTypes.SET_DROP_DOWN_KEY,
  payload: dropDownKey,
})

export const clearDropDownKey = () => ({
  type: PropensityActionTypes.CLEAR_DROP_DOWN_KEY,
})
export const clearFilters = () => ({
  type: PropensityActionTypes.CLEAR_PROPENSITY_FILTERS,
})
export const setIsExternalLoading = (isExternalLoading: boolean) => ({
  type: PropensityActionTypes.SET_IS_EXTERNAL_LOADING,
  payload: isExternalLoading,
})
export const clearIsExternalLoading = () => ({
  type: PropensityActionTypes.CLEAR_IS_EXTERNAL_LOADING,
})

export const setPageOneForPropensity = () => async (dispatch: AppDispatch) => {
  await dispatch(setPropensityPageNumber(1))
}

export const setUserConfigurations = (data: UserConfiguration) => ({
  type: PropensityActionTypes.SET_USER_CONFIGURATIONS,
  payload: data,
})

export const updateUserConfigurations =
  (partnerId: string, userId: string, data: Partial<UserConfiguration>) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.UPDATE_USER_CONFIGURATIONS))
    try {
      const payload = snakeize(data)
      const response = await putUserConfigurations(partnerId, userId, payload)
      if (response?.data) {
        const camelizedData = camelize(response.data) as UserConfiguration
        await dispatch(setUserConfigurations(camelizedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_USER_CONFIGURATIONS))
    }
  }

export const fetchAllIcpData =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_ALL_ICPS))
    try {
      const response = await fetchAllIcps(partnerId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response.data) as {
          total?: number
          icps?: (ICPData & { name?: string })[]
        }
        const mappedIcps =
          camelizedData.icps?.map(icp => ({
            ...icp,
            icpName: icp.icpName ?? icp.name ?? '',
          })) ?? null
        await dispatch(
          setAllIcpData({
            total: camelizedData.total ?? 0,
            icps: mappedIcps,
          })
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_ALL_ICPS))
    }
  }

export const updateIcpData =
  (
    partnerId: string,
    icpId: string,
    updates: { isActive?: boolean; isDefault?: boolean },
    successMessage?: string
  ) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<boolean> => {
    await dispatch(startLoading(LoadingTypes.UPDATE_ICP))
    try {
      await updateIcpState(partnerId, icpId, updates)

      dispatch(updateIcpInList(icpId, updates))

      if (updates.isDefault === true) {
        const state = getState()
        const allIcps = state.allIcpData.icps || []
        allIcps.forEach(icp => {
          if (icp.id !== icpId && icp.isDefault) {
            dispatch(updateIcpInList(icp.id!, { isDefault: false }))
          }
        })
      }

      dispatch(
        updateAppAlert({
          message: successMessage ?? DefaultSuccesrOnSubmitData,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return true
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )

      await dispatch(fetchAllIcpData(partnerId))
      return false
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_ICP))
    }
  }

export const fetchUserConfigurations =
  (partnerId: string, userId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_USER_CONFIGURATIONS))
    try {
      const response = await getUserConfigurations(partnerId, userId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response.data) as UserConfiguration
        await dispatch(setUserConfigurations(camelizedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_USER_CONFIGURATIONS))
    }
  }
